import React, { useState, useEffect, useMemo, useRef } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import apiClient from '../../utils/api';

const TYPE_COLORS = {
  person: '#6366f1',
  company: '#f59e0b',
  project: '#10b981',
  place: '#3b82f6',
  concept: '#8b5cf6',
  event: '#ec4899',
  unknown: '#6b7280',
};

const MemoryWikiBrowser = ({ selectedUserId }) => {
  const [index, setIndex] = useState(null);
  const [pages, setPages] = useState({});
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [viewMode, setViewMode] = useState('grouped');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const graphRef = useRef();

  useEffect(() => {
    if (selectedUserId) {
      loadIndex(selectedUserId);
      setSelectedSlug(null);
      setPageContent(null);
      setPages({});
    }
  }, [selectedUserId]);

  const loadIndex = async (userId) => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get(`/memory/admin/${userId}/wiki`);
      setIndex(response.data);
      // Pre-load all pages from the bulk response
      if (response.data.pages) {
        setPages(response.data.pages);
      }
    } catch (err) {
      console.error('Error loading wiki index:', err);
      setError('Failed to load wiki index');
      setIndex(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (slug) => {
    setSelectedSlug(slug);
    if (pages[slug]) {
      setPageContent(pages[slug]);
      return;
    }
    // Pages live in type folders, so the single-page endpoint needs the type too.
    const entityType = index?.entities?.[slug]?.type;
    if (!entityType) {
      setPageContent('*Page not found*');
      return;
    }
    try {
      const response = await apiClient.get(`/memory/admin/${selectedUserId}/wiki/${entityType}/${slug}`);
      setPages(prev => ({ ...prev, [slug]: response.data.content }));
      setPageContent(response.data.content);
    } catch (err) {
      console.error('Error loading wiki page:', err);
      setPageContent('*Page not found*');
    }
  };

  // Graph data from pre-computed relationships
  const graphData = useMemo(() => {
    if (!index?.entities) return { nodes: [], links: [] };

    const entities = index.entities;
    const nodes = Object.entries(entities).map(([slug, info]) => ({
      id: slug,
      name: info.name,
      type: info.type || 'unknown',
      factCount: info.fact_count || 0,
    }));

    // Use pre-computed relationships from the API
    const links = (index.relationships || []).map(r => ({
      source: r.source,
      target: r.target,
      type: r.type,
    }));

    return { nodes, links };
  }, [index]);

  // Group entities by type
  const groupedEntities = useMemo(() => {
    if (!index?.entities) return {};
    const groups = {};
    Object.entries(index.entities).forEach(([slug, info]) => {
      const type = info.type || 'unknown';
      if (!groups[type]) groups[type] = [];
      groups[type].push({ slug, ...info });
    });
    // Sort each group alphabetically
    Object.values(groups).forEach(list => list.sort((a, b) => a.name.localeCompare(b.name)));
    return groups;
  }, [index]);

  if (!selectedUserId) {
    return <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Select a user</div>;
  }

  if (loading) {
    return <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Loading wiki...</div>;
  }

  if (error) {
    return <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px' }}>{error}</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>🧠 Memory Wiki</h3>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {index?.entity_count || 0} entities
        </span>
      </div>

      {/* View toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'grouped', label: '🏷️ Grouped' },
          { id: 'graph', label: '🔗 Graph' },
        ].map(v => (
          <button key={v.id} onClick={() => { setViewMode(v.id); setSelectedSlug(null); }}
            style={{
              padding: '0.4rem 0.75rem', border: 'none', borderRadius: '6px',
              fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
              background: viewMode === v.id ? '#2563eb' : '#f3f4f6',
              color: viewMode === v.id ? 'white' : '#374151',
            }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Grouped view */}
      {viewMode === 'grouped' && (
        <div style={{ display: 'grid', gridTemplateColumns: selectedSlug ? '280px 1fr' : '1fr', gap: '1rem' }}>
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {Object.entries(groupedEntities).map(([type, entities]) => (
              <div key={type} style={{ marginBottom: '1rem' }}>
                <div style={{
                  fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                  color: TYPE_COLORS[type] || '#6b7280', marginBottom: '0.4rem',
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: TYPE_COLORS[type] || '#6b7280', display: 'inline-block',
                  }} />
                  {type} ({entities.length})
                </div>
                {entities.map(entity => (
                  <div key={entity.slug} onClick={() => loadPage(entity.slug)}
                    style={{
                      padding: '0.5rem 0.6rem', marginBottom: '0.3rem', cursor: 'pointer',
                      borderRadius: '5px', fontSize: '0.8rem',
                      background: selectedSlug === entity.slug ? '#eff6ff' : 'white',
                      border: selectedSlug === entity.slug ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    }}>
                    <span style={{ fontWeight: 600 }}>{entity.name}</span>
                    {entity.fact_count > 0 && (
                      <span style={{ fontSize: '0.65rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
                        {entity.fact_count} facts
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Page detail */}
          {selectedSlug && pageContent && (
            <PageDetail slug={selectedSlug} content={pageContent} onClose={() => setSelectedSlug(null)} onNavigate={loadPage} />
          )}
        </div>
      )}

      {/* Graph view */}
      {viewMode === 'graph' && (
        <div style={{ display: 'grid', gridTemplateColumns: selectedSlug ? '1fr 300px' : '1fr', gap: '1rem' }}>
          <div style={{
            border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden',
            background: '#1a1a2e', height: '480px', position: 'relative',
          }}>
            <ForceGraph2D
              ref={graphRef}
              graphData={graphData}
              nodeLabel={n => `${n.name} (${n.type})`}
              nodeRelSize={5}
              linkColor={() => '#4b5563'}
              linkDirectionalArrowLength={4}
              linkDirectionalArrowRelPos={1}
              linkWidth={1.5}
              linkLabel={l => l.type}
              onNodeClick={(node) => loadPage(node.id)}
              nodeCanvasObject={(node, ctx, globalScale) => {
                const isSelected = node.id === selectedSlug;
                const radius = isSelected ? 7 : 4 + Math.min(node.factCount, 5);
                const color = TYPE_COLORS[node.type] || '#6b7280';

                if (isSelected) {
                  ctx.beginPath();
                  ctx.arc(node.x, node.y, radius + 3, 0, 2 * Math.PI);
                  ctx.fillStyle = `${color}33`;
                  ctx.fill();
                }

                ctx.beginPath();
                ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
                ctx.fillStyle = color;
                ctx.fill();

                const fontSize = Math.max(10 / globalScale, 3);
                ctx.font = `${isSelected ? 'bold ' : ''}${fontSize}px Sans-Serif`;
                ctx.fillStyle = '#e5e7eb';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillText(node.name, node.x, node.y + radius + 2);
              }}
              height={480}
              backgroundColor="#1a1a2e"
              cooldownTicks={80}
            />
            {/* Legend */}
            <div style={{
              position: 'absolute', bottom: 8, left: 8, display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
              background: 'rgba(26,26,46,0.9)', padding: '0.3rem 0.5rem', borderRadius: '4px',
            }}>
              {Object.entries(TYPE_COLORS).filter(([t]) => groupedEntities[t]).map(([type, color]) => (
                <span key={type} style={{ fontSize: '0.6rem', color, fontWeight: 600 }}>● {type}</span>
              ))}
            </div>
          </div>

          {/* Page detail */}
          {selectedSlug && pageContent && (
            <PageDetail slug={selectedSlug} content={pageContent} onClose={() => setSelectedSlug(null)} onNavigate={loadPage} />
          )}
        </div>
      )}
    </div>
  );
};

// Reusable page detail panel
const PageDetail = ({ slug, content, onClose, onNavigate }) => {
  // Parse wiki links for navigation
  const renderContent = (text) => {
    const parts = text.split(/(\[\[[^\]]+\]\])/g);
    return parts.map((part, i) => {
      const match = part.match(/^\[\[([^\]]+)\]\]$/);
      if (match) {
        // Links are written as [[type/slug]]; pages are keyed by the bare slug.
        const target = match[1].includes('/') ? match[1].split('/').pop() : match[1];
        return (
          <span key={i} onClick={() => onNavigate(target)} role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter') onNavigate(target); }}
            style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>
            {match[1]}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div style={{
      padding: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb',
      borderRadius: '8px', maxHeight: '480px', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h4 style={{ margin: 0, fontSize: '0.85rem', fontFamily: 'monospace' }}>{slug}.md</h4>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
      </div>
      <pre style={{
        fontSize: '0.78rem', lineHeight: '1.7', whiteSpace: 'pre-wrap',
        wordBreak: 'break-word', fontFamily: 'monospace', margin: 0, color: '#374151',
      }}>
        {content.split('\n').map((line, i) => (
          <div key={i}>{renderContent(line)}</div>
        ))}
      </pre>
    </div>
  );
};

export default MemoryWikiBrowser;
